import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { houseCanonState } from "@/lib/carouselStudioMeta";
import { type CarouselDesignReview, type CarouselItem } from "@/lib/api";
import { cn } from "@/lib/utils";

const RATING_LABEL: Record<string, string> = {
  better: "lepsze",
  worse: "gorsze",
  same: "tak samo",
};

export type DesignProposal = {
  op?: string;
  label?: string;
  hint?: string;
  group?: string;
  choice?: string;
  index?: number;
  box?: string;
  recipe?: string;
};

export function reviewProposals(review?: CarouselDesignReview | null): DesignProposal[] {
  if (review?.proposed?.length) {
    return review.proposed.map((row) => ({
      ...row,
      label: row.label || row.op || "zmiana",
      choice: row.choice || "check",
    }));
  }
  const raw = review?.proposed_actions?.length ? review.proposed_actions : review?.actions || [];
  return raw.map((row) => ({
    op: String(row.op || ""),
    label: String(row.label || row.op || "zmiana"),
    choice: "check",
  }));
}

export function defaultProposalPicked(proposals: DesignProposal[]): Record<number, boolean> {
  const seen = new Set<string>();
  const next: Record<number, boolean> = {};
  proposals.forEach((row, i) => {
    if (row.choice === "always") {
      next[i] = true;
      return;
    }
    if (row.choice === "radio" && row.group) {
      next[i] = !seen.has(row.group);
      seen.add(row.group);
      return;
    }
    next[i] = true;
  });
  return next;
}

export function collectProposalSelected(
  proposals: DesignProposal[],
  picked: Record<number, boolean>,
): number[] {
  return proposals.flatMap((row, i) => {
    if (row.choice === "always" || picked[i]) return [i];
    return [];
  });
}

export function DesignProposalList({
  proposals,
  picked,
  onPicked,
  interactive,
}: {
  proposals: DesignProposal[];
  picked?: Record<number, boolean>;
  onPicked?: (next: Record<number, boolean>) => void;
  interactive?: boolean;
}) {
  if (!proposals.length) return null;
  if (!interactive) {
    return (
      <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
        {proposals.map((row, i) => (
          <li key={`${row.label}-${i}`}>
            <span>{row.label}</span>
            {row.hint ? <span className="block text-[11px]">{row.hint}</span> : null}
          </li>
        ))}
      </ul>
    );
  }
  const setPicked = onPicked || (() => undefined);
  const state = picked || {};
  const pickRadio = (group: string, index: number) => {
    const next = { ...state };
    proposals.forEach((row, i) => {
      if (row.choice === "radio" && row.group === group) next[i] = i === index;
    });
    setPicked(next);
  };
  return (
    <div className="space-y-2 text-xs">
      {proposals.map((row, i) => {
        const label = row.label || row.op || `zmiana ${i + 1}`;
        if (row.choice === "always") {
          return (
            <div key={`${label}-${i}`} className="rounded-sm bg-muted/40 px-2 py-1.5">
              <p className="font-medium text-foreground">{label}</p>
              <p className="text-[11px] text-muted-foreground">{row.hint || "Przy wdrożeniu idzie zawsze."}</p>
            </div>
          );
        }
        if (row.choice === "radio" && row.group) {
          return (
            <label key={`${label}-${i}`} className="flex items-start gap-2 text-muted-foreground">
              <input
                type="radio"
                className="mt-0.5"
                name={`design-recipe-${row.group}`}
                checked={Boolean(state[i])}
                onChange={() => pickRadio(row.group || "", i)}
              />
              <span>
                <span className="text-foreground">{label}</span>
                {row.hint ? <span className="mt-0.5 block text-[11px]">{row.hint}</span> : null}
              </span>
            </label>
          );
        }
        return (
          <label key={`${label}-${i}`} className="flex items-start gap-2 text-muted-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={state[i] !== false}
              onChange={(e) => setPicked({ ...state, [i]: e.target.checked })}
            />
            <span>
              <span className="text-foreground">{label}</span>
              {row.hint ? <span className="mt-0.5 block text-[11px]">{row.hint}</span> : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export function StudioOptionCard({
  title,
  hint,
  children,
  optional = true,
}: {
  title: string;
  hint: string;
  children?: ReactNode;
  optional?: boolean;
}) {
  if (optional) {
    return (
      <details className="group rounded-md border border-dashed border-slate-300 bg-slate-50/60 open:bg-white">
        <summary className="cursor-pointer list-none px-3 py-2.5 marker:content-none [&::-webkit-details-marker]:hidden">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              Opcjonalne
            </span>
            <span className="text-sm font-medium text-slate-800">{title}</span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
        </summary>
        <div className="space-y-3 border-t border-slate-200 px-3 py-3">{children}</div>
      </details>
    );
  }
  return (
    <div className="space-y-3 rounded-md border border-border bg-white px-3 py-3">
      <div>
        <p className="text-sm font-medium text-slate-800">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </div>
  );
}

export function HouseCanonBar({ item }: { item: CarouselItem }) {
  const state = houseCanonState(item.design_review, Boolean(item.slides?.length));
  if (!state.hasSlides) return null;
  if (state.stale) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
        Stary skład. Kliknij Złóż obrazki.
      </div>
    );
  }
  if (!state.score) return null;
  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2 text-sm",
        state.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800",
      )}
    >
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {state.rows.map((row) => (
          <span key={row.key} className={row.ok ? undefined : "font-medium"}>
            {row.label}
          </span>
        ))}
      </div>
      {item.design_review?.house_note ? (
        <p className="mt-1 text-xs">{item.design_review.house_note}</p>
      ) : null}
    </div>
  );
}

export function DesignAnalysisCard({
  item,
  pending,
  applyPending,
  onReview,
  onApply,
  extraAction,
}: {
  item: CarouselItem;
  pending: boolean;
  applyPending: boolean;
  onReview: () => void;
  onApply: (selected: number[]) => void;
  extraAction?: ReactNode;
}) {
  const review = item.design_review;
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const slides = item.slides || [];
  const proposals = reviewProposals(review);
  const waitingApply = review?.status === "pending" && proposals.length > 0;
  const busy = pending || applyPending;
  const applySelected = collectProposalSelected(proposals, picked);

  useEffect(() => {
    setPicked(defaultProposalPicked(proposals));
  }, [review?.status, review?.verdict, proposals.length]);

  return (
    <StudioOptionCard
      title="Analiza układu"
      hint="Nie rusza słów. Zmienia układ i czytelność względem tła: przepis, justowanie, marker, ramkę, odsunięcie od cienia / dziurek. Najpierw analiza, potem wdróż to, co zostawiasz."
    >
      {review?.error ? <p className="text-xs text-destructive">{review.error}</p> : null}
      {!proposals.length && !pending ? (
        <p className="text-xs text-muted-foreground">Nie było jeszcze analizy.</p>
      ) : null}
      {!!proposals.length ? (
        <DesignProposalList
          proposals={proposals}
          picked={picked}
          onPicked={setPicked}
          interactive={waitingApply}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={pending || !slides.length} onClick={onReview}>
          {pending ? "Analizuję…" : "Poproś o analizę"}
        </Button>
        {waitingApply ? (
          <Button
            size="sm"
            variant="brand"
            disabled={busy || !slides.length || applySelected.length === 0}
            onClick={() => onApply(applySelected)}
          >
            {applyPending ? "Wdrażam zaznaczone…" : "Wdróż zaznaczone"}
          </Button>
        ) : null}
      </div>
      {waitingApply ? (
        <p className="text-[11px] text-muted-foreground">
          Zaznacz, co wdrażamy. Odznaczone wraca do kolejnej tury.
          {proposals.some((row) => row.choice === "radio")
            ? " Dwa przepisy na jedną ścianę: wybierz jeden."
            : ""}
        </p>
      ) : null}
      {extraAction}
    </StudioOptionCard>
  );
}

export function DesignLayoutNoteCard({
  item,
  feedbackPending,
  onFeedback,
}: {
  item: CarouselItem;
  feedbackPending: boolean;
  onFeedback: (rating: string, text: string) => void;
}) {
  const review = item.design_review;
  const [note, setNote] = useState(item.design_feedback || review?.feedback || "");

  return (
    <StudioOptionCard
      title="Uwaga do rozkładu"
      hint="Co zmienić w rozłożeniu treści: hak, lewa oś, za dużo kart. To ocena tury, nie wdrażanie."
    >
      <textarea
        className="min-h-[72px] w-full rounded-md border border-input bg-transparent p-2 text-sm"
        value={note}
        maxLength={600}
        onChange={(e) => setNote(e.target.value)}
        placeholder="np. za dużo kart, hak za mały, content ma iść w lewo"
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={feedbackPending} onClick={() => onFeedback("", note)}>
          Zapisz uwagę
        </Button>
        {(["better", "worse", "same"] as const).map((rating) => (
          <Button
            key={rating}
            size="sm"
            variant={review?.rating === rating ? "brand" : "outline"}
            disabled={feedbackPending}
            onClick={() => onFeedback(rating, note)}
          >
            {RATING_LABEL[rating]}
          </Button>
        ))}
      </div>
    </StudioOptionCard>
  );
}

export default function DesignReviewPanel({ item }: { item: CarouselItem }) {
  return <HouseCanonBar item={item} />;
}
